import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { useEffect, useState, useRef } from "react";

/* ─────────────────────────────────────────────
   ROUTE COLOR LOGIC
───────────────────────────────────────────── */

function getRouteLevelEuCategory(segments) {
  if (!segments || segments.length === 0) return "out";
  const allInter = segments.every(s => s.is_inter_europe);
  if (allInter) return "inter";
  const hasEu = segments.some(s => s.is_inter_europe || s.is_mixed);
  if (hasEu) return "mixed";
  return "out";
}

function getRouteColor(segments) {
  const category = getRouteLevelEuCategory(segments);
  if (category === "inter") return "#67e8f9"; // 100% EU Coverage — cyan
  if (category === "mixed") return "#fcd34d"; // 50% EU Coverage  — amber
  return "#94a3b8";                           // Out of Scope      — grey
}

function getConnectionColor(conn) {
  if (conn.is_inter_europe) return "#67e8f9";
  if (conn.is_mixed) return "#fcd34d";
  return "#94a3b8";
}

function splitAntimeridian(coords) {
  if (!coords || coords.length === 0) return [];
  const segments = [];
  let current = [coords[0]];
  for (let i = 1; i < coords.length; i++) {
    let [lat1, lon1] = coords[i - 1];
    let [lat2, lon2] = coords[i];
    let dLon = lon2 - lon1;
    if (Math.abs(dLon) > 180) {
      if (dLon > 180) lon2 -= 360;
      else if (dLon < -180) lon2 += 360;
      const t = (180 - Math.abs(lon1)) / (Math.abs(lon2 - lon1));
      const latCross = lat1 + (lat2 - lat1) * t;
      const crossingLon = lon1 > 0 ? 180 : -180;
      const oppositeLon = lon1 > 0 ? -180 : 180;
      current.push([latCross, crossingLon]);
      segments.push(current);
      current = [[latCross, oppositeLon], [lat2, ((lon2 + 540) % 360) - 180]];
    } else {
      current.push([lat2, lon2]);
    }
  }
  if (current.length > 0) segments.push(current);
  return segments;
}

/* ───────────────────────────────────────────── */

function buildRouteArrows(splitSegments, map) {
  if (!splitSegments || !splitSegments.length || !map) return [];
  const arrows = [];
  const arrowCount = 5;
  const routeSegments = [];
  let totalDistance = 0;
  splitSegments.forEach(part => {
    for (let i = 0; i < part.length - 1; i++) {
      const start = part[i];
      const end = part[i + 1];
      const p1 = map.latLngToLayerPoint(start);
      const p2 = map.latLngToLayerPoint(end);
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      routeSegments.push({ start, end, p1, p2, dist, cumulativeStart: totalDistance });
      totalDistance += dist;
    }
  });
  if (totalDistance === 0) return [];
  for (let a = 1; a <= arrowCount; a++) {
    const targetDistance = (a / (arrowCount + 1)) * totalDistance;
    const segment = routeSegments.find(seg =>
      targetDistance >= seg.cumulativeStart && targetDistance <= seg.cumulativeStart + seg.dist
    );
    if (!segment) continue;
    const localDistance = targetDistance - segment.cumulativeStart;
    const t = localDistance / segment.dist;
    const lat = segment.start[0] + (segment.end[0] - segment.start[0]) * t;
    const lng = segment.start[1] + (segment.end[1] - segment.start[1]) * t;
    const dx = segment.p2.x - segment.p1.x;
    const dy = segment.p2.y - segment.p1.y;
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;
    arrows.push({ position: [lat, lng], angle });
  }
  return arrows;
}

function RouteArrows({ segment, color, opacity = 1 }) {
  const map = useMap();
  return (
    <>
      {buildRouteArrows(splitAntimeridian(segment.coords), map).map((arrow, idx) => (
        <Marker
          key={segment.id + "-arrow-" + idx}
          position={arrow.position}
          interactive={false}
          icon={L.divIcon({
            className: "route-arrow-icon",
            html: `<div class="route-arrow-shape" style="transform:rotate(${arrow.angle}deg);color:${color};opacity:${opacity};"></div>`,
            iconSize: [40, 20],
            iconAnchor: [20, 2]
          })}
        />
      ))}
    </>
  );
}

/* ── MAP CONTROLS OVERLAY ──────────────────────────────────────────────── */

const TILE_LAYERS = {
  standard: { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", label: "🗺 Standard" },
  satellite: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", label: "🛰 Satellite" },
};

function MapControls({ showPortNames, onTogglePortNames, showArrows, onToggleArrows, tileKey, onSetTile, routeOpacity, onSetOpacity, onFitRoutes, hasRoutes, forceOpen, onPanelRef }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  // Tour can force the panel open/closed
  useEffect(() => {
    if (forceOpen !== undefined) setOpen(forceOpen);
  }, [forceOpen]);

  // Register panel element in tourRefs whenever it mounts/unmounts
  useEffect(() => {
    onPanelRef?.(open ? panelRef.current : null);
  }, [open, onPanelRef]);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleOutsideClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  const panelStyle = {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "14px 16px",
    minWidth: 210,
    color: "#0f172a",
    fontFamily: "var(--font-mono, monospace)",
    fontSize: 11,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
  };

  const toggleBtnStyle = {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    color: "#475569",
    fontSize: 15,
    width: 34,
    height: 34,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
  };

  function Toggle({ value, onToggle }) {
    return (
      <div
        onClick={onToggle}
        style={{
          width: 36, height: 18, borderRadius: 9,
          background: value ? "#0891b2" : "#cbd5e1",
          position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0,
        }}
      >
        <div style={{
          position: "absolute", top: 2, left: value ? 20 : 2,
          width: 14, height: 14, borderRadius: "50%", background: "#fff",
          transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }} />
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      style={{
        position: "absolute", top: 12, right: 12, zIndex: 1000,
        display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6,
      }}
      onMouseDown={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
      onTouchStart={e => e.stopPropagation()}
      onWheel={e => e.stopPropagation()}
    >
      <button onClick={() => setOpen(v => !v)} title="Map controls" style={toggleBtnStyle}>
        ⚙
      </button>

      {open && (
        <div style={panelStyle}>

          {/* Port names */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ color: "#475569" }}>Port names</span>
            <Toggle value={showPortNames} onToggle={onTogglePortNames} />
          </div>

          {/* Route arrows */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ color: "#475569" }}>Route arrows</span>
            <Toggle value={showArrows} onToggle={onToggleArrows} />
          </div>

          {/* Tile layer */}
          <div>
            <div style={{ color: "#94a3b8", marginBottom: 6, letterSpacing: "1px", textTransform: "uppercase", fontSize: 9 }}>Map Style</div>
            <div style={{ display: "flex", gap: 6 }}>
              {Object.entries(TILE_LAYERS).map(([key, { label }]) => (
                <button
                  key={key}
                  onClick={() => onSetTile(key)}
                  style={{
                    flex: 1, padding: "4px 0", fontSize: 10, borderRadius: 4, cursor: "pointer",
                    background: tileKey === key ? "rgba(8,145,178,0.08)" : "#f8fafc",
                    border: `1px solid ${tileKey === key ? "#0891b2" : "#e2e8f0"}`,
                    color: tileKey === key ? "#0891b2" : "#64748b",
                    fontFamily: "var(--font-mono, monospace)",
                    fontWeight: tileKey === key ? 600 : 400,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Route opacity */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
              <span style={{ color: "#475569" }}>Route opacity</span>
              <span style={{ color: "#0891b2", fontWeight: 600 }}>{Math.round(routeOpacity * 100)}%</span>
            </div>
            <input
              type="range" min="0.1" max="1" step="0.05"
              value={routeOpacity}
              onChange={e => onSetOpacity(parseFloat(e.target.value))}
              onMouseDown={e => e.stopPropagation()}
              onPointerDown={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
              style={{ width: "100%", accentColor: "#0891b2", cursor: "pointer" }}
            />
          </div>

          {/* Fit to routes */}
          {hasRoutes && (
            <button
              onClick={() => { onFitRoutes(); setOpen(false); }}
              style={{
                background: "rgba(8,145,178,0.07)", border: "1px solid rgba(8,145,178,0.3)",
                borderRadius: 4, color: "#0891b2", fontSize: 10, padding: "5px 0",
                cursor: "pointer", fontFamily: "var(--font-mono, monospace)",
              }}
            >
              ⊕ Fit to routes
            </button>
          )}

          {/* Reset view */}
          <button
            onClick={() => { onFitRoutes(true); setOpen(false); }}
            style={{
              background: "#f8fafc", border: "1px solid #e2e8f0",
              borderRadius: 4, color: "#64748b", fontSize: 10, padding: "5px 0",
              cursor: "pointer", fontFamily: "var(--font-mono, monospace)",
            }}
          >
            ↺ Reset view
          </button>

        </div>
      )}
    </div>
  );
}

/* inner component that can use useMap */
function MapControlsInner({ showPortNames, onTogglePortNames, showArrows, onToggleArrows, tileKey, onSetTile, routeOpacity, onSetOpacity, selectedRoutes, buildingSegments, forceOpen, onPanelRef }) {
  const map = useMap();

  function fitRoutes(reset) {
    if (reset) { map.setView([20, 0], 2); return; }
    const allCoords = [];
    selectedRoutes.forEach(r => {
      r.segments.forEach(seg => {
        (seg.coords || []).forEach(c => allCoords.push(c));
      });
    });
    buildingSegments.forEach(seg => {
      (seg.coords || []).forEach(c => allCoords.push(c));
    });
    if (allCoords.length > 0) {
      map.fitBounds(L.latLngBounds(allCoords), { padding: [40, 40] });
    }
  }

  const hasRoutes = selectedRoutes.length > 0 || buildingSegments.length > 0;

  return (
    <MapControls
      showPortNames={showPortNames}
      onTogglePortNames={onTogglePortNames}
      showArrows={showArrows}
      onToggleArrows={onToggleArrows}
      tileKey={tileKey}
      onSetTile={onSetTile}
      routeOpacity={routeOpacity}
      onSetOpacity={onSetOpacity}
      onFitRoutes={fitRoutes}
      hasRoutes={hasRoutes}
      forceOpen={forceOpen}
      onPanelRef={onPanelRef}
    />
  );
}

/* ── EXPLORE INFO CARD ─────────────────────────────────────────────────── */

function ExploreCard({ port, outgoing, incoming, onClose }) {
  const cardRef = useRef(null);
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
  }, []);

  if (!port) return null;

  const euLabel = c => {
    if (c.is_inter_europe) return { text: "100% EU", color: "#67e8f9" };
    if (c.is_mixed) return { text: "50% EU", color: "#fcd34d" };
    return { text: "Out", color: "#94a3b8" };
  };

  const Section = ({ title, connections, dotStyle }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6, marginBottom: 6,
        fontSize: 9, letterSpacing: "1.2px", textTransform: "uppercase", color: "#64748b",
      }}>
        <div style={dotStyle} />
        {title} ({connections.length})
      </div>
      {connections.length === 0 ? (
        <div style={{ color: "#94a3b8", fontSize: 10, paddingLeft: 14 }}>None</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 3, paddingLeft: 14 }}>
          {connections.map(c => {
            const eu = euLabel(c);
            const peer = title.startsWith("Out") ? c.to : c.from;
            return (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: eu.color, fontSize: 9, fontWeight: 700, minWidth: 44 }}>{eu.text}</span>
                <span style={{ color: "#1e293b", fontSize: 10 }}>{peer}</span>
                <span style={{ color: "#94a3b8", fontSize: 9, marginLeft: "auto" }}>
                  {Math.round(c.distance)} nm
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={cardRef}
      style={{
        position: "absolute", top: 12, left: 12, zIndex: 1001,
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: "14px 16px",
        minWidth: 230, maxWidth: 270,
        fontFamily: "var(--font-mono, monospace)",
        fontSize: 11,
        boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
        pointerEvents: "all",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{port.name}</div>
          <div style={{ fontSize: 9, color: "#94a3b8", letterSpacing: "1px", textTransform: "uppercase", marginTop: 2 }}>
            Explore mode — click any port
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#94a3b8", fontSize: 14, lineHeight: 1, padding: 2,
          }}
        >✕</button>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: "#f1f5f9", marginBottom: 10 }} />

      {/* Legend */}
      <div style={{ display: "flex", gap: 12, marginBottom: 10, fontSize: 9, color: "#64748b" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" stroke="#0891b2" strokeWidth="2"/></svg>
          Outgoing
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="22" height="8">
            <line x1="0" y1="4" x2="22" y2="4" stroke="#8b5cf6" strokeWidth="2" strokeDasharray="4,3"/>
          </svg>
          Incoming
        </div>
      </div>

      <Section
        title="Outgoing routes"
        connections={outgoing}
        dotStyle={{ width: 8, height: 2, background: "#0891b2", borderRadius: 1 }}
      />
      <Section
        title="Incoming routes"
        connections={incoming}
        dotStyle={{ width: 8, height: 2, background: "transparent", borderRadius: 1, borderTop: "2px dashed #8b5cf6" }}
      />
    </div>
  );
}


function MapView({
  ports,
  connections,
  route,
  availableConnections,
  selectedRoutes,
  onStart,
  onAddSegment,
  onUndo,
  onFinish,
  onSetDays,
  exploreMode,
  onToggleExploreMode,
  forceMapControls,
  onMapControlsRef,
}) {
  const [showPortNames, setShowPortNames] = useState(true);
  const [showArrows, setShowArrows] = useState(true);
  const [tileKey, setTileKey] = useState("standard");
  const [routeOpacity, setRouteOpacity] = useState(0.9);

  // ── Explore mode ──
  const [explorePortId, setExplorePortId] = useState(null);

  // Reset explored port when explore mode is turned off
  useEffect(() => {
    if (!exploreMode) setExplorePortId(null);
  }, [exploreMode]);

  const explorePort = explorePortId ? ports.find(p => p.id === explorePortId) : null;
  const exploreOutgoing = explorePortId ? (connections || []).filter(c => c.from === explorePortId) : [];
  const exploreIncoming = explorePortId ? (connections || []).filter(c => c.to === explorePortId) : [];

  function isPortSelectable(portId) {
    if (exploreMode) return true; // every port is clickable in explore mode
    if (route.status === "building" && !route.startPort) return true;
    if (route.status === "idle") return false;
    if (route.status !== "building") return false;
    return availableConnections.some(conn => conn.to === portId);
  }

  function getConnectionTo(portId) {
    return availableConnections.find(conn => conn.to === portId);
  }

  function getIcon(isSelectable, isCurrent, isExplored, portName) {
    // Explored port — purple, larger
    if (isExplored) {
      return new L.DivIcon({
        className: "",
        html: `
          <div style="position:relative;display:flex;flex-direction:column;align-items:center;">
            <div style="width:18px;height:18px;background:#8b5cf6;border:2.5px solid #ffffff;border-radius:50%;box-shadow:0 0 0 4px rgba(139,92,246,0.22),0 0 10px rgba(139,92,246,0.4);"></div>
            <div style="margin-top:3px;background:#ffffff;color:#6d28d9;padding:2px 6px;border-radius:3px;font-size:9px;white-space:nowrap;font-family:monospace;border:1px solid #ddd6fe;box-shadow:0 1px 3px rgba(0,0,0,0.1);">${portName}</div>
          </div>`,
        iconSize: [90, 38],
        iconAnchor: [45, 18],
      });
    }

    // Explore mode — all ports selectable, style as "hoverable" amber dots
    if (exploreMode) {
      return new L.DivIcon({
        className: "",
        html: `
          <div style="position:relative;display:flex;flex-direction:column;align-items:center;">
            <div style="width:10px;height:10px;background:#a78bfa;border:1.5px solid #ffffff;border-radius:50%;box-shadow:0 0 0 2px rgba(167,139,250,0.2);cursor:pointer;"></div>
            ${showPortNames ? `<div style="margin-top:3px;background:#ffffff;color:#6d28d9;padding:1px 5px;border-radius:3px;font-size:9px;white-space:nowrap;font-family:monospace;border:1px solid #ddd6fe;">${portName}</div>` : ""}
          </div>`,
        iconSize: showPortNames ? [80, 28] : [10, 10],
        iconAnchor: showPortNames ? [40, 10] : [5, 5],
      });
    }

    if (isCurrent) {
      return new L.DivIcon({
        className: "",
        html: `
          <div style="position:relative;display:flex;flex-direction:column;align-items:center;">
            <div style="width:16px;height:16px;background:#0891b2;border:2px solid #ffffff;border-radius:50%;box-shadow:0 0 0 3px rgba(8,145,178,0.2),0 0 8px rgba(8,145,178,0.4);"></div>
            ${showPortNames ? `<div style="margin-top:3px;background:#ffffff;color:#0891b2;padding:1px 5px;border-radius:3px;font-size:9px;white-space:nowrap;font-family:monospace;border:1px solid #e2e8f0;box-shadow:0 1px 3px rgba(0,0,0,0.1);">${portName}</div>` : ""}
          </div>`,
        iconSize: showPortNames ? [80, 36] : [16, 16],
        iconAnchor: showPortNames ? [40, 16] : [8, 8],
      });
    }

    if (isSelectable) {
      return new L.DivIcon({
        className: "",
        html: `
          <div style="position:relative;display:flex;flex-direction:column;align-items:center;">
            <div style="width:12px;height:12px;background:#f59e0b;border:2px solid #ffffff;border-radius:50%;box-shadow:0 0 0 2px rgba(245,158,11,0.2);"></div>
            ${showPortNames ? `<div style="margin-top:3px;background:#ffffff;color:#92400e;padding:1px 5px;border-radius:3px;font-size:9px;white-space:nowrap;font-family:monospace;border:1px solid #fde68a;box-shadow:0 1px 3px rgba(0,0,0,0.1);">${portName}</div>` : ""}
          </div>`,
        iconSize: showPortNames ? [80, 30] : [12, 12],
        iconAnchor: showPortNames ? [40, 12] : [6, 6],
      });
    }

    return new L.DivIcon({
      className: "",
      html: `
        <div style="position:relative;display:flex;flex-direction:column;align-items:center;">
          <div style="width:10px;height:10px;background:#94a3b8;border:1.5px solid #ffffff;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,0.15);"></div>
          ${showPortNames ? `<div style="margin-top:3px;background:#ffffff;color:#475569;padding:1px 4px;border-radius:3px;font-size:9px;white-space:nowrap;font-family:monospace;border:1px solid #e2e8f0;box-shadow:0 1px 3px rgba(0,0,0,0.08);">${portName}</div>` : ""}
        </div>`,
      iconSize: showPortNames ? [80, 28] : [10, 10],
      iconAnchor: showPortNames ? [40, 10] : [5, 5],
    });
  }

  const tileUrl = TILE_LAYERS[tileKey]?.url || TILE_LAYERS.standard.url;

  return (
    <MapContainer
        center={[20, 0]}
        zoom={2}
        minZoom={2}
        maxBounds={[[-85, -180], [85, 180]]}
        maxBoundsViscosity={1.0}
        worldCopyJump={false}
        style={{ height: "100vh", width: "100%", position: "relative" }}
        zoomControl={true}
      >
      <TileLayer url={tileUrl} noWrap={true} key={tileKey} />

      {/* Map controls overlay */}
      <MapControlsInner
        showPortNames={showPortNames}
        onTogglePortNames={() => setShowPortNames(v => !v)}
        showArrows={showArrows}
        onToggleArrows={() => setShowArrows(v => !v)}
        tileKey={tileKey}
        onSetTile={setTileKey}
        routeOpacity={routeOpacity}
        onSetOpacity={setRouteOpacity}
        selectedRoutes={selectedRoutes || []}
        buildingSegments={route.segments}
        forceOpen={forceMapControls}
        onPanelRef={onMapControlsRef}
      />

      {/* Explore info card */}
      {exploreMode && (
        <ExploreCard
          port={explorePort}
          outgoing={exploreOutgoing}
          incoming={exploreIncoming}
          onClose={() => { onToggleExploreMode(); setExplorePortId(null); }}
        />
      )}

      {/* Ports */}
      {ports.map(port => {
        const selectable = isPortSelectable(port.id);
        const isCurrent = !exploreMode && port.id === route.currentPort;
        const isExplored = exploreMode && port.id === explorePortId;

        return (
          <Marker
            key={port.id + "-" + showPortNames + "-" + exploreMode + "-" + (explorePortId === port.id)}
            position={port.position}
            icon={getIcon(selectable, isCurrent, isExplored, port.name)}
            eventHandlers={{
              click: () => {
                if (exploreMode) {
                  setExplorePortId(port.id);
                  return;
                }
                if (!selectable) return;
                if (route.status === "building" && !route.startPort) {
                  onStart(port.id);
                  return;
                }
                if (route.status === "building") {
                  const conn = getConnectionTo(port.id);
                  if (conn) onAddSegment(conn);
                }
              },
            }}
          />
        );
      })}

      {/* ── EXPLORE MODE ROUTES ── */}
      {exploreMode && explorePortId && (
        <>
          {/* Outgoing — solid line, EU-colored */}
          {exploreOutgoing.map(conn => {
            const color = getConnectionColor(conn);
            return splitAntimeridian(conn.coords).map((part, i) => (
              <Polyline
                key={conn.id + "-out-" + i}
                positions={part}
                pathOptions={{ color, weight: 2.5, opacity: 0.85 }}
              />
            ));
          })}

          {/* Incoming — dashed purple, reversed coords */}
          {exploreIncoming.map(conn => {
            const reversedCoords = [...conn.coords].reverse();
            return splitAntimeridian(reversedCoords).map((part, i) => (
              <Polyline
                key={conn.id + "-in-" + i}
                positions={part}
                pathOptions={{
                  color: "#8b5cf6",
                  weight: 2,
                  opacity: 0.7,
                  dashArray: "6, 6",
                }}
              />
            ));
          })}
        </>
      )}

      {/* Current building route */}
      {!exploreMode && route.segments.map(seg => (
        <>
          {splitAntimeridian(seg.coords).map((part, i) => (
            <Polyline
              key={seg.id + "-part-" + i}
              positions={part}
              pathOptions={{ color: "#0891b2", weight: 2.5, opacity: routeOpacity }}
            />
          ))}
          {showArrows && <RouteArrows segment={seg} color="#0891b2" opacity={routeOpacity} />}
        </>
      ))}

      {/* All selected saved routes */}
      {(selectedRoutes || []).map(savedRoute => {
        const routeColor = getRouteColor(savedRoute.segments);
        return savedRoute.segments.map(seg => (
          <>
            {splitAntimeridian(seg.coords).map((part, i) => (
              <Polyline
                key={"saved-" + seg.id + "-part-" + i}
                positions={part}
                pathOptions={{ color: routeColor, weight: 3, opacity: routeOpacity }}
              />
            ))}
            {showArrows && <RouteArrows segment={seg} color={routeColor} opacity={routeOpacity} />}
          </>
        ));
      })}

      {/* Candidate routes (build mode) */}
      {!exploreMode && route.startPort &&
        availableConnections.map(conn =>
          splitAntimeridian(conn.coords).map((part, i) => (
            <Polyline
              key={conn.id + "-cand-" + i}
              positions={part}
              pathOptions={{ dashArray: "4, 8", color: "#fbbf24", weight: 1.5, opacity: 0.5 }}
            />
          ))
        )}

      {!exploreMode && (
        <FloatingCard
          ports={ports}
          route={route}
          onUndo={onUndo}
          onFinish={onFinish}
          onSetDays={onSetDays}
        />
      )}
    </MapContainer>
  );
}

export default MapView;

/* ─── FLOATING CARD ─── */

function FloatingCard({ ports, route, onUndo, onFinish, onSetDays }) {
  const map = useMap();
  const [position, setPosition] = useState(null);

  const currentIndex = route.ports.length - 1;
  const currentPortObj = route.ports[currentIndex];
  const currentPort = ports.find(p => p.id === currentPortObj?.id);

  useEffect(() => {
    function updatePosition() {
      if (!currentPort) return;
      const point = map.latLngToContainerPoint(currentPort.position);
      setPosition(point);
    }
    updatePosition();
    map.on("move", updatePosition);
    map.on("zoom", updatePosition);
    return () => {
      map.off("move", updatePosition);
      map.off("zoom", updatePosition);
    };
  }, [map, currentPort]);

  if (route.status !== "building" || !position || !currentPort) return null;

  return (
    <div
      className="floating-card"
      style={{ position: "absolute", top: position.y - 120, left: position.x + 16, zIndex: 1000 }}
    >
      <div>
        <div className="floating-card-port">{currentPort.name}</div>
        <div className="floating-card-label">
          {route.ports.length > 1 ? "Current Port" : "Origin Port"}
        </div>
      </div>
      {route.ports.length > 1 && (
        <div className="floating-card-actions">
          <button className="float-btn float-btn-undo" onClick={onUndo}>↩ Undo</button>
          <button className="float-btn float-btn-finish" onClick={onFinish}>✓ Finish</button>
        </div>
      )}
    </div>
  );
}